import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './ProductsPage.css'; // Assurez-vous que ce fichier CSS existe et est lié
import CriticalDatesCalendar from '../components/CriticalDatesCalendar'; // Votre composant calendrier
import { authHeader } from '../services/authService';
// Constantes
const CRITICAL_STOCK_THRESHOLD = 5;
const VAT_RATE = 0.20;
const UNCATEGORIZED_LABEL = 'Produits non catégorisés';

function ProductsPage() {
  // --- States ---
  const [products, setProducts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [name, setName] = useState('');
  const [stock, setStock] = useState(0);
  const [dailyPrice, setDailyPrice] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [identifier, setIdentifier] = useState('');
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false); // Chargement général
  const [error, setError] = useState(null);
  const [allCategories, setAllCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [isUpdatingPositions, setIsUpdatingPositions] = useState(false); // Chargement mise à jour positions
  const [openedCalendarProductId, setOpenedCalendarProductId] = useState(null);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [lightboxImageUrl, setLightboxImageUrl] = useState(null);

  // --- Fonctions Lightbox ---
  const openLightbox = (imageUrl) => {
    setLightboxImageUrl(imageUrl);
    setIsLightboxOpen(true);
  };
  const closeLightbox = () => {
    setIsLightboxOpen(false);
    setLightboxImageUrl(null);
  };

  // --- Fonctions Fetch ---
  const fetchProducts = useCallback(async (showLoading = true) => {
    if (showLoading) setIsLoading(true);
    setError(null);
    let url = 'https://backend.ouest-pro-event.fr/admin/products_admin';
    if (categoryFilter) {
      url += `?category_id=${categoryFilter}`;
    }
    // Le backend DOIT trier par position
    try {
      const res = await fetch(url,{
        headers: {
          ...authHeader()  // Ajout du header d'autorisation
        }
      });
      if (!res.ok) {
        throw new Error('Erreur lors de la récupération des produits');
      }
      const data = await res.json();
      setProducts(data); // L'ordre est crucial
    } catch (error) {
      setError(error.message);
      setProducts([]);
    } finally {
      if (showLoading) setIsLoading(false);
    }
  }, [categoryFilter]);

  const fetchCategories = async () => {
    try {
      const res = await fetch('https://backend.ouest-pro-event.fr/admin/categories');
      if (!res.ok) {
        throw new Error('Erreur lors de la récupération des catégories');
      }
      const data = await res.json();
      setAllCategories(data);
    } catch (error) {
      setError(prev => prev ? `${prev}\n${error.message}` : error.message);
    }
  };

  // --- useEffects ---
  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (allCategories.length > 0 || !categoryFilter) {
      fetchProducts();
    }
  }, [categoryFilter, allCategories, fetchProducts]);

  // --- Calcul des produits groupés ---
  const groupedProductsData = useMemo(() => {
    const groups = {};
    products.forEach(product => {
      const categoryDisplay = (product.categoryPaths?.length > 0 && product.categoryPaths[0].length > 0)
                            ? product.categoryPaths[0].join(' > ')
                            : UNCATEGORIZED_LABEL;
      if (!groups[categoryDisplay]) { groups[categoryDisplay] = []; }
      groups[categoryDisplay].push(product);
    });
    const sortedCategoryNames = Object.keys(groups).sort((a, b) => {
      if (a === UNCATEGORIZED_LABEL) return 1;
      if (b === UNCATEGORIZED_LABEL) return -1;
      return a.localeCompare(b);
    });
    return { groups, sortedCategoryNames };
  }, [products]);

  // --- Fonctions utilitaires ---
  const getAvailabilityStatus = (stockValue) => {
    if (stockValue > CRITICAL_STOCK_THRESHOLD) return { text: 'Disponible', colorClass: 'badge-green' };
    if (stockValue > 0) return { text: 'Stock Faible', colorClass: 'badge-orange' };
    return { text: 'Indisponible', colorClass: 'badge-red' };
  };

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = []; const previews = [];
    files.forEach(file => {
      if (!file.type.startsWith('image/')) { alert(`Le fichier ${file.name} n'est pas une image valide.`); return; }
      if (file.size > 5 * 1024 * 1024) { alert(`Le fichier ${file.name} dépasse la taille maximale de 5MB.`); return; }
      const isDuplicate = selectedPhotos.some(selectedFile => selectedFile.name === file.name && selectedFile.size === file.size);
      if (isDuplicate) { alert(`Le fichier ${file.name} est déjà sélectionné.`); return; }
      validFiles.push(file); previews.push(URL.createObjectURL(file));
    });
    setSelectedPhotos(prev => [...prev, ...validFiles]);
    setPhotoPreviews(prev => [...prev, ...previews]);
  };

  useEffect(() => {
    return () => { photoPreviews.forEach(src => URL.revokeObjectURL(src)); };
  }, [photoPreviews]);

  const handleRemoveSelectedPhoto = (index) => {
    URL.revokeObjectURL(photoPreviews[index]);
    const updatedPhotos = [...selectedPhotos]; const updatedPreviews = [...photoPreviews];
    updatedPhotos.splice(index, 1); updatedPreviews.splice(index, 1);
    setSelectedPhotos(updatedPhotos); setPhotoPreviews(updatedPreviews);
  };

  const getFullImageUrl = (relativeUrl) => {
    if (!relativeUrl) return null;
    if (relativeUrl.startsWith('http')) return relativeUrl;
    return `https://backend.ouest-pro-event.fr${relativeUrl}`;
  };

  // --- Fonctions CRUD ---
  // const handleCreate = async () => {
  //   if (!name.trim()) { setError("Le nom du produit est obligatoire."); return; }
  //   setIsLoading(true); setError(null);
  //   try {
  //     const category = allCategories.find(c => c.id.toString() === selectedCategory);
  //     const categoryName = category ? category.name : null;
  //     const payload = { name: name.trim(), identifier: identifier.trim() || null, description, stock, dailyPrice, categoryPaths: categoryName ? [[categoryName]] : [] };
  //     const method = editingProduct ? 'PUT' : 'POST';
  //     const url = editingProduct ? `https://backend.ouest-pro-event.fr/admin/products/${editingProduct.id}` : `https://backend.ouest-pro-event.fr/admin/products`;
  //     const res = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  //     if (!res.ok) { const errData = await res.json().catch(() => ({})); throw new Error(errData.error || `Erreur ${res.status}`); }
  //     const resData = await res.json();
  //     const productId = editingProduct ? editingProduct.id : resData.id;
  //     if (selectedPhotos.length > 0) {
  //       const formData = new FormData();
  //       selectedPhotos.forEach(photo => formData.append('photos', photo));
  //       const photoRes = await fetch(`https://backend.ouest-pro-event.fr/admin/products/${productId}/photos`, { method: 'POST', body: formData });
  //       if (!photoRes.ok) { console.error("Erreur upload photos"); /* Gérer l'erreur */ }
  //     }
  //     alert(editingProduct ? "Produit mis à jour" : "Produit créé");
  //     // Reset form
  //     setName(''); setStock(0); setDailyPrice(0); setSelectedCategory("");
  //     setSelectedPhotos([]); setPhotoPreviews([]); setIdentifier(''); setDescription('');
  //     setEditingProduct(null);
  //     await fetchProducts(false);
  //   } catch (error) { setError(error.message); }
  //   finally { setIsLoading(false); }
  // };

  const handleCreate = async () => {
    if (!name.trim()) { setError("Le nom du produit est obligatoire."); return; }
    setIsLoading(true); setError(null);
    try {
      const category = allCategories.find(c => c.id.toString() === selectedCategory);
      const categoryName = category ? category.name : null;
      const payload = { name: name.trim(), identifier: identifier.trim() || null, description, stock, dailyPrice, categoryPaths: categoryName ? [[categoryName]] : [] };
      const method = editingProduct ? 'PUT' : 'POST';
      const url = editingProduct ? `https://backend.ouest-pro-event.fr/admin/products/${editingProduct.id}` : `https://backend.ouest-pro-event.fr/admin/products`;
      
      // Ajout des en-têtes d'authentification
      const res = await fetch(url, { 
        method, 
        headers: { 
          'Content-Type': 'application/json',
          ...authHeader()  // Ajout de l'en-tête d'authentification
        }, 
        body: JSON.stringify(payload) 
      });
      
      if (!res.ok) { const errData = await res.json().catch(() => ({})); throw new Error(errData.error || `Erreur ${res.status}`); }
      const resData = await res.json();
      const productId = editingProduct ? editingProduct.id : resData.id;
      
      // Ajout des en-têtes d'authentification pour l'upload de photos également
      if (selectedPhotos.length > 0) {
        const formData = new FormData();
        selectedPhotos.forEach(photo => formData.append('photos', photo));
        const photoRes = await fetch(`https://backend.ouest-pro-event.fr/admin/products/${productId}/photos`, { 
          method: 'POST', 
          headers: {
            ...authHeader()  // Ajout de l'en-tête d'authentification
          },
          body: formData 
        });
        if (!photoRes.ok) { console.error("Erreur upload photos"); /* Gérer l'erreur */ }
      }
      
      alert(editingProduct ? "Produit mis à jour" : "Produit créé");
      // Reset form
      setName(''); setStock(0); setDailyPrice(0); setSelectedCategory("");
      setSelectedPhotos([]); setPhotoPreviews([]); setIdentifier(''); setDescription('');
      setEditingProduct(null);
      await fetchProducts(false);
    } catch (error) { setError(error.message); }
    finally { setIsLoading(false); }
};

  const handleEdit = (product) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setError(null);
    setEditingProduct(product);
    setName(product.name); setStock(product.stock); setDailyPrice(product.dailyPrice);
    setIdentifier(product.identifier || ''); setDescription(product.description || '');
    if (product.categoryPaths?.length > 0 && product.categoryPaths[0].length > 0 && allCategories.length > 0) {
        const categoryName = product.categoryPaths[0][product.categoryPaths[0].length - 1];
        const category = allCategories.find(c => c.name === categoryName);
        setSelectedCategory(category ? category.id.toString() : "");
    } else { setSelectedCategory(""); }
    setPhotoPreviews([]); setSelectedPhotos([]);
  };

  // const handleDelete = async (productId) => {
  //   if (!window.confirm("Vraiment supprimer ce produit ?")) return;
  //   setIsLoading(true); setError(null);
  //   try {
  //     const res = await fetch(`https://backend.ouest-pro-event.fr/admin/products/${productId}`, { method: 'DELETE' });
  //     if (!res.ok) { const errData = await res.json().catch(() => ({})); throw new Error(errData.error || `Erreur ${res.status}`); }
  //     alert("Produit supprimé");
  //     await fetchProducts(false);
  //     if (editingProduct && editingProduct.id === productId) {
  //       // Reset form if deleting the product being edited
  //       setName(''); setStock(0); setDailyPrice(0); setSelectedCategory("");
  //       setSelectedPhotos([]); setPhotoPreviews([]); setIdentifier(''); setDescription('');
  //       setEditingProduct(null);
  //     }
  //   } catch (error) { setError(error.message); }
  //   finally { setIsLoading(false); }
  // };

  const handleDelete = async (productId) => {
    if (!window.confirm("Vraiment supprimer ce produit ?")) return;
    setIsLoading(true); 
    setError(null);
    try {
      const res = await fetch(`https://backend.ouest-pro-event.fr/admin/products/${productId}`, { 
        method: 'DELETE',
        headers: {
          ...authHeader()  // Ajout de l'en-tête d'authentification
        }
      });
      
      if (!res.ok) { 
        const errData = await res.json().catch(() => ({})); 
        throw new Error(errData.error || `Erreur ${res.status}`); 
      }
      
      alert("Produit supprimé");
      await fetchProducts(false);
      if (editingProduct && editingProduct.id === productId) {
        // Reset form if deleting the product being edited
        setName(''); setStock(0); setDailyPrice(0); setSelectedCategory("");
        setSelectedPhotos([]); setPhotoPreviews([]); setIdentifier(''); setDescription('');
        setEditingProduct(null);
      }
    } catch (error) { 
      setError(error.message); 
    } finally { 
      setIsLoading(false); 
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!editingProduct || !window.confirm("Supprimer cette photo ?")) return;
    setIsLoading(true); setError(null);
    try {
      const res = await fetch(`https://backend.ouest-pro-event.fr/admin/products/${editingProduct.id}/photos/${photoId}`, { method: 'DELETE' });
      if (!res.ok) { const errData = await res.json().catch(() => ({})); throw new Error(errData.error || `Erreur ${res.status}`); }
      alert("Photo supprimée");
      const updatedPhotos = editingProduct.photos.filter(photo => photo.id !== photoId);
      setEditingProduct(prev => ({ ...prev, photos: updatedPhotos }));
      setProducts(prevProducts => prevProducts.map(p =>
        p.id === editingProduct.id ? { ...p, photos: updatedPhotos } : p
      ));
    } catch (error) { setError(error.message); }
    finally { setIsLoading(false); }
  };

  // --- Logique pour déplacer les produits ---
  // const handleMoveProduct = useCallback(async (productId, direction) => {
  //   setError(null);
  //   let currentCategoryName = null;
  //   let currentIndex = -1;
  //   let productsInCategory = [];

  //   // Trouver produit, catégorie, index
  //   for (const categoryName of groupedProductsData.sortedCategoryNames) {
  //     const group = groupedProductsData.groups[categoryName];
  //     const index = group.findIndex(p => p.id === productId);
  //     if (index !== -1) {
  //       currentCategoryName = categoryName; currentIndex = index; productsInCategory = group; break;
  //     }
  //   }
  //   if (currentIndex === -1) { setError("Erreur : produit introuvable."); return; }

  //   // Calculer nouvel index et valider
  //   const targetIndex = currentIndex + (direction === 'up' ? -1 : 1);
  //   if (targetIndex < 0 || targetIndex >= productsInCategory.length) return;

  //   // Mise à jour Optimiste
  //   const updatedCategoryList = [...productsInCategory];
  //   [updatedCategoryList[currentIndex], updatedCategoryList[targetIndex]] = [updatedCategoryList[targetIndex], updatedCategoryList[currentIndex]];
  //   const newProductsState = [];
  //   groupedProductsData.sortedCategoryNames.forEach(catName => {
  //     newProductsState.push(...(catName === currentCategoryName ? updatedCategoryList : groupedProductsData.groups[catName]));
  //   });
  //   setProducts(newProductsState); // Mise à jour UI

  //   // Préparer et appeler l'API
  //   const positionUpdates = [
  //     { id: updatedCategoryList[targetIndex].id, position: targetIndex },
  //     { id: updatedCategoryList[currentIndex].id, position: currentIndex }
  //   ];
  //   setIsUpdatingPositions(true);
  //   try {
  //     const res = await fetch('https://backend.ouest-pro-event.fr/admin/products/positions', {
  //       method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(positionUpdates),
  //     });
  //     if (!res.ok) { const errData = await res.json().catch(() => ({})); throw new Error(errData.error || `Erreur API ${res.status}`); }
  //     // Succès
  //   } catch (apiError) {
  //     setError(`Erreur API : ${apiError.message}. Ordre annulé.`);
  //     await fetchProducts(false); // Rollback
  //   } finally {
  //     setIsUpdatingPositions(false);
  //   }
  // }, [groupedProductsData, fetchProducts]);

  const handleMoveProduct = useCallback(async (productId, direction) => {
    setError(null);
    let currentCategoryName = null;
    let currentIndex = -1;
    let productsInCategory = [];
  
    // Trouver produit, catégorie, index
    for (const categoryName of groupedProductsData.sortedCategoryNames) {
      const group = groupedProductsData.groups[categoryName];
      const index = group.findIndex(p => p.id === productId);
      if (index !== -1) {
        currentCategoryName = categoryName; currentIndex = index; productsInCategory = group; break;
      }
    }
    if (currentIndex === -1) { setError("Erreur : produit introuvable."); return; }
  
    // Calculer nouvel index et valider
    const targetIndex = currentIndex + (direction === 'up' ? -1 : 1);
    if (targetIndex < 0 || targetIndex >= productsInCategory.length) return;
  
    // Mise à jour Optimiste
    const updatedCategoryList = [...productsInCategory];
    [updatedCategoryList[currentIndex], updatedCategoryList[targetIndex]] = [updatedCategoryList[targetIndex], updatedCategoryList[currentIndex]];
    const newProductsState = [];
    groupedProductsData.sortedCategoryNames.forEach(catName => {
      newProductsState.push(...(catName === currentCategoryName ? updatedCategoryList : groupedProductsData.groups[catName]));
    });
    setProducts(newProductsState); // Mise à jour UI
  
    // Préparer et appeler l'API
    const positionUpdates = [
      { id: updatedCategoryList[targetIndex].id, position: targetIndex },
      { id: updatedCategoryList[currentIndex].id, position: currentIndex }
    ];
    setIsUpdatingPositions(true);
    try {
      const res = await fetch('https://backend.ouest-pro-event.fr/admin/products/positions', {
        method: 'PUT', 
        headers: { 
          'Content-Type': 'application/json',
          ...authHeader()  // Ajout de l'en-tête d'authentification
        }, 
        body: JSON.stringify(positionUpdates),
      });
      if (!res.ok) { 
        const errData = await res.json().catch(() => ({})); 
        throw new Error(errData.error || `Erreur API ${res.status}`); 
      }
      // Succès
    } catch (apiError) {
      setError(`Erreur API : ${apiError.message}. Ordre annulé.`);
      await fetchProducts(false); // Rollback
    } finally {
      setIsUpdatingPositions(false);
    }
  }, [groupedProductsData, fetchProducts]);


  const handleToggleVisibility = useCallback(async (productId) => {
    setError(null);
    try {
      const res = await fetch(`https://backend.ouest-pro-event.fr/admin/products/${productId}/toggle-visibility`, { 
        method: 'PUT',
        headers: {
          ...authHeader()  // Ajout de l'en-tête d'authentification
        }
      });
      
      if (!res.ok) { 
        const errData = await res.json().catch(() => ({})); 
        throw new Error(errData.error || `Erreur ${res.status}`); 
      }
      
      // Mettre à jour localement l'état des produits pour refléter le changement
      const data = await res.json();
      setProducts(prevProducts => prevProducts.map(p => 
        p.id === productId ? { ...p, isHidden: data.isHidden } : p
      ));
      
    } catch (error) { 
      setError(error.message); 
    }
  }, []);

  // --- Rendu JSX ---
  return (
    <div className="products-page">
      <header className="page-header">
        <h1>Gestion des Produits</h1>
        {error && <div className="error-alert">{error}</div>}
        {isUpdatingPositions && <div className="loading-indicator">Mise à jour des positions...</div>}
      </header>

      <div className="main-content">
        {/* Section Formulaire */}
        <section className="product-form-section">
           <div className="card">
             <div className="card-header"><h2>{editingProduct ? "Modifier le produit" : "Ajouter un produit"}</h2></div>
             <div className="card-body">
               {isLoading && !isUpdatingPositions && <div className="loader-overlay"><div className="loader"></div></div>}
               <form onSubmit={(e) => { e.preventDefault(); handleCreate(); }}>
                  {/* Form Grid */}
                  <div className="form-grid">
                    <div className="form-group"><label htmlFor="productIdentifier">Identifiant</label><input id="productIdentifier" type="text" value={identifier} onChange={e => setIdentifier(e.target.value)} /></div>
                    <div className="form-group"><label htmlFor="productName">Nom *</label><input id="productName" type="text" value={name} onChange={e => setName(e.target.value)} required /></div>
                    <div className="form-group full-width"><label htmlFor="productDescription">Description</label><textarea id="productDescription" value={description} onChange={e => setDescription(e.target.value)} rows={4}/></div>
                    <div className="form-group"><label htmlFor="productStock">Stock *</label><input id="productStock" type="number" value={stock} onChange={e => setStock(parseInt(e.target.value, 10) || 0)} min="0" required/></div>
                    <div className="form-group"><label htmlFor="productPrice">Prix HT (€) *</label><input id="productPrice" type="number" value={dailyPrice} onChange={e => setDailyPrice(parseFloat(e.target.value) || 0)} min="0" step="0.01" required/></div>
                    <div className="form-group"><label htmlFor="productCategory">Catégorie</label><select id="productCategory" value={selectedCategory || ""} onChange={e => setSelectedCategory(e.target.value)}><option value="">-- Sélectionner --</option>{allCategories.map(cat => (<option key={cat.id} value={cat.id}>{cat.full_path || cat.name}</option>))}</select></div>
                  </div>
                  {/* Photos Section */}
                   <div className="photos-section">
                     <h3>Photos</h3>
                     <div className="photos-upload"><label htmlFor="photoInput" className="btn btn-secondary btn-sm">Choisir</label><input id="photoInput" type="file" multiple accept="image/*" onChange={handlePhotoChange} style={{ display: 'none' }} /><small className="help-text">Max 5MB.</small></div>
                     {photoPreviews.length > 0 && (<div className="photos-preview"><h4>Nouvelles</h4><div className="photos-grid">{photoPreviews.map((src, index) => (<div key={index} className="photo-item new"><img src={src} alt="" /><button type="button" className="btn-icon delete" onClick={() => handleRemoveSelectedPhoto(index)}>×</button></div>))}</div></div>)}
                     {editingProduct?.photos?.length > 0 && (<div className="current-photos"><h4>Actuelles</h4><div className="photos-grid">{editingProduct.photos.map(photo => (<div key={photo.id} className="photo-item current"><img src={getFullImageUrl(photo.url)} alt="" /><button type="button" className="btn-icon delete" onClick={() => handleDeletePhoto(photo.id)} disabled={isLoading}>×</button></div>))}</div></div>)}
                   </div>
                   {/* Form Actions */}
                  <div className="form-actions"><button type="submit" className="btn btn-success" disabled={isLoading || isUpdatingPositions || !name.trim()}>{isLoading ? '...' : (editingProduct ? "Enregistrer" : "Créer")}</button>{editingProduct && (<button type="button" className="btn btn-secondary" disabled={isLoading || isUpdatingPositions} onClick={() => {setEditingProduct(null); setName(''); setStock(0); setDailyPrice(0); setSelectedCategory(""); setIdentifier(''); setDescription(''); setSelectedPhotos([]); setPhotoPreviews([]); setError(null);}}>Annuler</button>)}</div>
               </form>
             </div>
           </div>
         </section>

        {/* Section Liste des Produits */}
        <section className="products-list-section">
          <div className="card">
            <div className="card-header list-header">
              <h2>Liste {isUpdatingPositions ? '(Maj...)' : ''}</h2>
              <div className="category-filter">
                <label htmlFor="categoryFilter">Filtrer:</label>
                <select id="categoryFilter" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} disabled={isLoading || isUpdatingPositions || allCategories.length === 0}>
                  <option value="">Toutes</option>
                  {allCategories.map(cat => ( <option key={cat.id} value={cat.id}> {cat.full_path || cat.name} </option> ))}
                </select>
              </div>
            </div>
            <div className="card-body table-container">
              {isLoading && !products.length && !isUpdatingPositions ? ( <div className="loader-overlay"><div className="loader"></div></div> ) : (
                <table className="products-table">
                  <thead>
                    <tr>
                      {/* Colonne pour les flèches */}
                      <th style={{ width: '60px', textAlign: 'center' }}>Ordre</th>
                      <th>Identifiant</th>
                      <th>Photos</th>
                      <th>Nom</th>
                      <th>Description</th>
                      <th>Prix HT</th>
                      <th>Prix TTC</th>
                      <th>Stock</th>
                      <th>Dispo.</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  {/* Itération sur les catégories */}
                  {!isLoading && groupedProductsData.sortedCategoryNames.length === 0 ? (
                     <tbody><tr> <td colSpan={10} style={{ textAlign: 'center', padding: '20px' }}> Aucun produit {categoryFilter ? "pour cette catégorie" : ""}. </td> </tr></tbody>
                  ) : (
                    groupedProductsData.sortedCategoryNames.map(categoryName => (
                      <tbody key={categoryName} className="category-group">
                        {/* Séparateur de catégorie */}
                        <tr className="category-separator-row"><td colSpan={10} className="category-separator-cell">{categoryName}</td></tr>
                        {/* Produits de la catégorie */}
                        {groupedProductsData.groups[categoryName].map((product, index, arr) => {
                          const availability = getAvailabilityStatus(product.stock);
                          const priceTTC = (product.dailyPrice * (1 + VAT_RATE)).toFixed(2);
                          const isFirstItem = index === 0;
                          const isLastItem = index === arr.length - 1;
                          return (
                            <tr key={product.id} className="product-row">
                              {/* Cellule pour les flèches */}
                              <td className="order-actions-cell">
                                <div className="order-buttons-container">
                                  <button className="btn btn-sm btn-move btn-move-up" title="Monter" onClick={() => handleMoveProduct(product.id, 'up')} disabled={isFirstItem || isLoading || isUpdatingPositions} aria-label={`Monter ${product.name}`}> ↑ </button>
                                  <button className="btn btn-sm btn-move btn-move-down" title="Descendre" onClick={() => handleMoveProduct(product.id, 'down')} disabled={isLastItem || isLoading || isUpdatingPositions} aria-label={`Descendre ${product.name}`}> ↓ </button>
                                </div>
                              </td>
                              {/* Données produit */}
                              <td className="product-identifier-cell">{product.identifier || '-'}</td>
                              <td className="product-photo-cell"><div className="thumbnail-container">{product.photos?.slice(0, 3).map(photo => { const url = getFullImageUrl(photo.url); return url && <img key={photo.id} src={url} alt="" className="product-table-thumbnail" onClick={() => openLightbox(url)} />;}) || <div className="no-photo-placeholder">N/A</div>}</div></td>
                              <td className="product-name-cell">
                                {product.name}

                              {product.isHidden && <span className="visibility-badge">Caché</span>}
                              </td>

                              <td className="product-description-cell" title={product.description || ''}>{product.description || '-'}</td>
                              <td>{product.dailyPrice.toFixed(2)} €</td>
                              <td>{priceTTC} €</td>
                              <td>{product.stock}</td>
                              <td><span className={`badge ${availability.colorClass}`} onClick={() => setOpenedCalendarProductId(product.id)} title="Voir calendrier">{availability.text}</span></td>
                              {/* Actions Modifier/Supprimer */}
                              <td><div className="action-buttons"><button className="btn btn-edit btn-sm" onClick={() => handleEdit(product)} disabled={isLoading || isUpdatingPositions} title="Modifier">Modifier</button>
                              <button 
      className={`btn ${product.isHidden ? 'btn-success' : 'btn-warning'} btn-sm`} 
      onClick={() => handleToggleVisibility(product.id)} 
      disabled={isLoading || isUpdatingPositions} 
      title={product.isHidden ? "Rendre visible" : "Cacher"}
    >
      {product.isHidden ? "Afficher" : "Cacher"}
    </button>
                              <button className="btn btn-delete btn-sm" onClick={() => handleDelete(product.id)} disabled={isLoading || isUpdatingPositions} title="Supprimer">Supprimer</button></div></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    ))
                  )}
                </table>
              )}
            </div>
          </div>
        </section>
      </div>

      {/* --- Modals --- */}
      {openedCalendarProductId && (<div className="calendar-modal-overlay" onClick={() => setOpenedCalendarProductId(null)}><div className="calendar-modal-content" onClick={e => e.stopPropagation()}><button className="calendar-modal-close" onClick={() => setOpenedCalendarProductId(null)} title="Fermer">×</button><h3>Calendrier</h3><CriticalDatesCalendar productId={openedCalendarProductId} /></div></div>)}
      {isLightboxOpen && lightboxImageUrl && (<div className="lightbox-overlay" onClick={closeLightbox}><div className="lightbox-content" onClick={e => e.stopPropagation()}><button className="lightbox-close" onClick={closeLightbox} title="Fermer">×</button><img src={lightboxImageUrl} alt="Agrandissement"/></div></div>)}
    </div>
  );
}

export default ProductsPage;